// One source of truth for every TanStack Query key tuple. Components and
// hooks always go through this factory; raw `['tasks', projectId]` arrays
// elsewhere are a bug (typo'd 'task' vs 'tasks' silently breaks invalidation).
//
// `as const` on every return value preserves the tuple shape so React
// Query's `QueryKey` overloads narrow correctly.

export const queryKeys = {
  // Tasks
  tasksFor: (projectId: string) => ["tasks", projectId] as const,
  task: (taskId: string) => ["task", taskId] as const,
  subtasksOf: (taskId: string) => ["subtasks", taskId] as const,
  taskDeps: (taskId: string) => ["task", taskId, "deps"] as const,
  milestonesFor: (projectId: string) => ["milestones", projectId] as const,
  allMilestones: () => ["milestones", "all"] as const,
  epicsFor: (projectId: string) => ["epics", projectId] as const,
  projectDeps: (projectId: string) => ["project-deps", projectId] as const,
  documentsFor: (projectId: string) => ["documents", projectId] as const,
  document: (id: string) => ["document", id] as const,
  requirements: (projectId: string) => ["requirements", projectId] as const,
  customFields: (projectId: string) => ["custom-fields", projectId] as const,
  customFieldValues: (projectId: string) =>
    ["custom-field-values", projectId] as const,
  reactions: (targetType: string, targetId: string) =>
    ["reactions", targetType, targetId] as const,
  tasksAll: () => ["tasks", "all"] as const,

  // Personal queues
  me: () => ["me"] as const,
  meTasks: () => ["me", "tasks"] as const,
  meWatching: () => ["me", "watching"] as const,

  // Inbox
  announcements: () => ["announcements"] as const,
  teams: () => ["teams"] as const,
  inbox: () => ["inbox"] as const,
  inboxUnread: () => ["inbox", "unread"] as const,

  // Project surfaces
  projects: () => ["projects"] as const,
  project: (projectId: string) => ["projects", projectId] as const,
  // Full member roster for a project (Members tab + Settings page).
  projectMembers: (projectId: string) =>
    ["project", projectId, "members"] as const,
  activityFor: (projectId: string) => ["activity", projectId] as const,
  activityGlobal: () => ["activity", "global"] as const,
  // Per-task activity feed (TaskDetail Activity tab).
  taskActivity: (taskId: string) => ["task", taskId, "activity"] as const,
  // Dashboard preview uses a flat useQuery (not useInfiniteQuery), so it gets
  // a distinct key. Sharing the activityGlobal key crashes InfiniteQueryObserver
  // when transitioning from the dashboard to the /activity page.
  activityGlobalDashboard: () => ["activity", "global", "dashboard"] as const,

  // Comments + tags + attachments
  commentsOf: (taskId: string) => ["comments", taskId] as const,
  attachmentsForTask: (taskId: string) =>
    ["attachments", "task", taskId] as const,
  attachmentsForComment: (commentId: string) =>
    ["attachments", "comment", commentId] as const,
  tags: () => ["tags"] as const,

  // Team + files + branding
  team: () => ["team"] as const,
  teamMember: (userId: string) => ["team", userId] as const,
  files: (opts?: {
    projectId?: string;
    type?: string;
    uploader?: string;
    q?: string;
    sort?: string;
    dir?: string;
    page?: number;
    pageSize?: number;
  }) => ["files", opts ?? {}] as const,
  // T49: project Files tab (project-scoped files + task attachments)
  projectFiles: (projectId: string) => ["files", "project", projectId] as const,
  branding: () => ["branding"] as const,
  capabilities: () => ["capabilities"] as const,

  // Admin surfaces
  adminUsers: () => ["admin", "users"] as const,
  adminInvites: (status?: "pending" | "all") =>
    ["admin", "invites", status ?? "pending"] as const,

  // Dashboard
  metricsDashboard: () => ["metrics", "dashboard"] as const,
  teamWorkload: () => ["team", "workload"] as const,
  projectsRecent: (limit: number) => ["projects", "recent", limit] as const,

  // Profile
  profileStats: () => ["me", "stats"] as const,
  myProjects: () => ["me", "projects"] as const,

  // Notification preferences
  notificationPrefs: () => ["me", "notification-prefs"] as const,

  // Sessions
  mySessions: () => ["me", "sessions"] as const,

  // Workspace
  workspace: () => ["workspace"] as const,
};
